import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import CustomLabel from "./CustomLabel";
import {useTheme} from "@emotion/react";
import {
    BROADCAST_ON_SUBMIT_FIND_EMAIL,
    BROADCAST_ON_SUBMIT_FIND_PASSWORD,
    BROADCAST_ON_SUBMIT_LOGIN,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import {lang} from "../../../lang";
import ErrorMsg from "./ErrorMsg";
import {autoHyphenBirthDate, autoHyphenPhoneNumber} from "../../../common";

/*
    props
    type : string = input type
    max_length : number = input maxLength
    name : string = input name
    placeholder : string = input placeholder
    on_change : fnc(return void) =  부모 컴포넌트가 가진 info(ref)를 변경
    need_label : bool = 라벨의 필요 여부
    label_name : string = 라벨명
    read_only : bool = 수정 불가 여부
    current_value : string|number = 부모 컴포넌트가 가진 info(ref)의 현재 값
    is_error : bool = 부모 컴포넌트에서 validate체크 후 error발생 여부
    regex : string = 정규식
 */
const CustomInput = (props) => {
    // props 초기화
    const {
        type = "text",
        max_length = null,
        name,
        placeholder = null,
        on_change = null,
        need_label = true,
        label_name = null,
        read_only = false,
        current_value = null,
        is_error = false,
        regex = null,
    } = props;

    const {broadcast} = useApplicationContext();

    // state
    const [is_filled, setIsFilled] = useState(false);
    const [is_not_validate, setIsNotValidate] = useState(is_error);
    const [error_msg, setErrorMsg] = useState(current_value.error_msg);

    // DOM ref
    const input_ref = useRef();

    // css style
    const {view} = useComponentStyle({read_only, is_filled, is_not_validate});

    useEffect(() => {
        if (is_error) {
            input_ref.current.focus();
        }
    }, [current_value.value, is_error]);

    const onEnterSubmit = () => {
        if (name === "password") {
            // 비밀번호 입력에서 엔터 누름 -> LoginForm의 validateInputValue 실행
            broadcast.send(BROADCAST_ON_SUBMIT_LOGIN, true);
        } else if (name === "phone") {
            // 휴대폰 입력에서 엔터 누름 -> FindEmailForm의 validateInputValue 실행
            broadcast.send(BROADCAST_ON_SUBMIT_FIND_EMAIL, true);
        } else if (name === "account") {
            // 이메일 입력에서 엔터 누름 -> FindPasswordForm의 validateInputValue 실행
            broadcast.send(BROADCAST_ON_SUBMIT_FIND_PASSWORD, true);
        }
    };

    const setCheckErrorMsg = () => {
        switch (name) {
            case "account":
                current_value.error_msg = lang.login.check_login_email;
                setErrorMsg(lang.login.check_login_email);
                return;
            case "password":
                current_value.error_msg = lang.login.check_login_password;
                setErrorMsg(lang.login.check_login_password);
                return;
            case "name":
                current_value.error_msg = lang.modify_info.check_modify_name;
                setErrorMsg(lang.modify_info.check_modify_name);
                return;
            case "phone":
                current_value.error_msg = lang.modify_info.check_modify_phone;
                setErrorMsg(lang.modify_info.check_modify_phone);
                return;
            case "birth":
                current_value.error_msg = lang.modify_info.check_modify_birth;
                setErrorMsg(lang.modify_info.check_modify_birth);
                return;
            default:
                return;
        }
    };
    const setEmptyErrorMsg = () => {
        switch (name) {
            case "account":
                current_value.error_msg = lang.login.empty_login_email;
                setErrorMsg(lang.login.empty_login_email);
                return;
            case "password":
                current_value.error_msg = lang.login.empty_login_password;
                setErrorMsg(lang.login.empty_login_password);
                return;
            case "name":
                current_value.error_msg = lang.modify_info.empty_modify_name;
                setErrorMsg(lang.modify_info.empty_modify_name);
                return;
            case "phone":
                current_value.error_msg = lang.modify_info.empty_modify_phone;
                setErrorMsg(lang.modify_info.empty_modify_phone);
                return;
            case "birth":
                current_value.error_msg = lang.modify_info.empty_modify_birth;
                setErrorMsg(lang.modify_info.empty_modify_birth);
                return;
            default:
                return;
        }
    };

    const onHandleError = (value) => {
        if (value.length < 1) {
            setEmptyErrorMsg();
            setIsNotValidate(true);
        } else {
            if (!regex.test(value)) {
                setCheckErrorMsg();
                setIsNotValidate(true);
            } else {
                current_value.error_msg = null;
                setErrorMsg(null);
                setIsFilled(true);
                setIsNotValidate(false);
            }
        }
    };

    const onBirthAndPhoneKeyUp = (value) => {
        if (name === "birth" && value.length >= 1) {
            input_ref.current.value = autoHyphenBirthDate(value);
            on_change(autoHyphenBirthDate(value));
        } else if (name === "phone" && value.length >= 1) {
            input_ref.current.value = autoHyphenPhoneNumber(value);
            on_change(autoHyphenPhoneNumber(value));
        }
    };

    return (
        <view.InputWrapper>
            {need_label && <CustomLabel htmlFor={`id_${name}`} label_name={label_name} ref={input_ref} />}
            <view.Input
                autoComplete={"off"}
                ref={input_ref}
                id={`id_${name}`}
                name={name}
                readOnly={read_only}
                defaultValue={current_value.value}
                type={type}
                maxLength={max_length}
                placeholder={placeholder}
                onKeyPress={(e) => {
                    if (e.key === "Enter") {
                        onEnterSubmit();
                    }
                }}
                onKeyUp={(e) => {
                    onBirthAndPhoneKeyUp(e.currentTarget.value);

                    if (is_error) {
                        if (name === "birth" || name === "phone") {
                            onHandleError(e.currentTarget.value);
                        }
                    }
                }}
                onBlur={(e) => {
                    current_value.value && !is_not_validate ? setIsFilled(true) : setIsFilled(false);

                    // name의 경우 onKeyUp이 아닌 blur시에 정규식 테스트
                    if (is_error) {
                        if (name === "name") {
                            onHandleError(e.currentTarget.value);
                        }
                    }
                }}
                onChange={(e) => {
                    if (name !== "birth" || name !== "phone") {
                        on_change(e.currentTarget.value);

                        if (is_error && name !== "name") {
                            onHandleError(e.currentTarget.value);
                        }
                    }
                }}
            />
            {is_not_validate && <ErrorMsg error_msg={error_msg} />}
        </view.InputWrapper>
    );
};

export default CustomInput;

export const useComponentStyle = ({read_only, is_filled, is_not_validate}) => {
    const theme = useTheme();
    return {
        view: {
            InputWrapper: styled.div({
                position: "relative",
                marginBottom: "1.5em",
                span: {
                    position: "absolute",
                    width: "100%",
                    left: "0",
                    bottom: "-1.3em",
                },
            }),
            Input: styled.input({
                marginTop: ".5em",
                boxSizing: "border-box",
                width: "100%",
                height: "2em",
                outline: "none",
                border: "none",
                borderBottom: read_only
                    ? "none"
                    : is_filled
                    ? `1px solid ${theme.colors.black}`
                    : is_not_validate
                    ? "1px solid red"
                    : `1px solid ${theme.colors.lg3}`,
                fontSize: theme.font_size.f16,
                "&:focus": read_only
                    ? {borderBottom: "none"}
                    : is_not_validate
                    ? {borderBottom: "1px solid red"}
                    : {borderBottom: `1px solid ${theme.colors.bearu_primary}`},
            }),
        },
    };
};
